import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Flame, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getGuest, saveGuest } from "@/lib/guest";

const nice = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function HoldDatesDialog({ open, onClose, slug, range, days, estimate = "", onSuccess }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const known = !editing && !!getGuest();

  useEffect(() => {
    if (open) {
      const g = getGuest();
      if (g) setForm(g);
      setEditing(false);
    }
  }, [open]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Name, phone and email are required to place a hold");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/public/date-holds", {
        property_slug: slug || "",
        start: range.start,
        end: range.end,
        estimate,
        ...form,
      });
      saveGuest(form);
      setDone(res.data);
      onSuccess?.();
    } catch (err) {
      toast.error("Could not place the hold. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => { onClose(); setDone(null); setEditing(false); };
  const expiry = done?.expires_at
    ? new Date(done.expires_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
    : "";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-md bg-ivory border-ink/10" data-testid="hold-dates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light flex items-center gap-2.5">
            <Flame className="w-5 h-5" style={{ color: "#C2410C" }} />
            {done ? "Your hold is in place" : "Hold these dates for 48 hours"}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="space-y-4 text-sm text-graphite leading-relaxed" data-testid="hold-success">
            {done.already ? (
              <p>You already have an active hold on these dates — no need to place another. Our planner will be in touch shortly.</p>
            ) : (
              <p>
                <Check className="w-4 h-4 inline mr-1 -mt-0.5 text-emerald-700" />
                <b className="text-ink">{nice(range.start)} → {nice(range.end)}</b> is marked for you until <b className="text-ink">{expiry}</b>. Our planner will call you shortly to take it forward.
              </p>
            )}
            <p className="text-xs bg-bone border border-ink/10 px-4 py-3 leading-relaxed">
              A hold isn&apos;t a confirmed booking — we accept bookings on a <b>first-come, first-served</b> basis. The first guest to confirm with an advance secures the dates, so don&apos;t wait too long.
            </p>
            <button onClick={close} className="btn-primary w-full" data-testid="hold-close-btn">Done</button>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <p className="text-sm text-graphite leading-relaxed">
              We&apos;ll place a complimentary 48-hour hold on <b className="text-ink">{nice(range.start)} → {nice(range.end)}</b> ({days} day{days === 1 ? "" : "s"}).
            </p>
            {known ? (
              <div className="bg-bone border border-ink/10 px-4 py-3 text-sm" data-testid="hold-known-guest">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-ink">{form.name}</div>
                    <div className="text-xs text-graphite mt-0.5">{form.phone} · {form.email}</div>
                  </div>
                  <button type="button" onClick={() => setEditing(true)} className="text-graphite hover:text-ink inline-flex items-center gap-1 text-xs" data-testid="hold-edit-details">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Input value={form.name} onChange={onChange("name")} placeholder="Your name *" required data-testid="hold-name" />
                <Input value={form.phone} onChange={onChange("phone")} placeholder="Phone *" required data-testid="hold-phone" />
                <Input value={form.email} onChange={onChange("email")} placeholder="Email *" type="email" required data-testid="hold-email" />
              </>
            )}
            <p className="text-xs text-graphite leading-relaxed">
              A hold isn&apos;t a booking — dates go to whoever confirms first with an advance. <b className="text-ink">First come, first served.</b>
            </p>
            <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-60" data-testid="hold-submit-btn">
              {submitting ? "Placing hold…" : "Place my 48-hour hold"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
